import type { TenantInfo } from '../lib/api';
import { SELECTED_TENANT_KEY, type StoredTenant } from '../lib/firebase';

const DEFAULT_BRAND = '#2563eb';

/**
 * Apply a tenant's brand color as a CSS custom property and persist the full
 * selection (code/databaseId/name/logoUrl/primaryColor) to localStorage so the
 * Firestore named-DB binding survives the next reload.
 */
export function applyBranding(t: TenantInfo): void {
  const color = t.primaryColor || DEFAULT_BRAND;
  try {
    document.documentElement.style.setProperty('--brand', color);
  } catch {
    // SSR / non-DOM contexts — ignore
  }
  persistSelection(t);
}

/** Persist the active tenant under the shared localStorage contract. */
export function persistSelection(t: TenantInfo): void {
  const stored: StoredTenant = {
    code: t.code,
    databaseId: t.code, // named Firestore DB id == tenant code
    name: t.name || t.code,
    logoUrl: t.logoUrl,
    primaryColor: t.primaryColor,
  };
  try {
    localStorage.setItem(SELECTED_TENANT_KEY, JSON.stringify(stored));
  } catch {
    // storage unavailable — ignore
  }
}

/** Reset brand color and clear the persisted selection (used on sign-out). */
export function clearBranding(): void {
  try {
    document.documentElement.style.setProperty('--brand', DEFAULT_BRAND);
  } catch {
    /* ignore */
  }
  try {
    localStorage.removeItem(SELECTED_TENANT_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * A tenant-specific host pins the workspace and skips the tenant picker — e.g.
 * cs.bekesher.net always opens the bekesher inbox, even for a multi-tenant admin
 * who would otherwise be asked to choose. Add hosts here as tenants get their
 * own agent domain.
 */
const HOST_TENANT: Record<string, string> = {
  'cs.bekesher.net': 'bekesher',
};
export function hostForcedTenant(): string | undefined {
  try {
    return HOST_TENANT[window.location.hostname.toLowerCase()];
  } catch {
    return undefined;
  }
}

/** Read the persisted selection, if any. */
export function readSelection(): StoredTenant | null {
  try {
    const raw = localStorage.getItem(SELECTED_TENANT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredTenant>;
    if (parsed && typeof parsed.code === 'string' && parsed.code) {
      return {
        code: parsed.code,
        databaseId: parsed.databaseId || parsed.code,
        name: parsed.name || parsed.code,
        logoUrl: parsed.logoUrl,
        primaryColor: parsed.primaryColor,
      };
    }
  } catch {
    /* ignore */
  }
  return null;
}
